import { Controller } from "@hotwired/stimulus"
import { logger } from "../logger"

// Connects to data-controller="header-tabs-scroll"
//
// Manages scrollable tabs - scrolls active tab into view and provides arrow navigation
//
export default class extends Controller {
  static targets = ["container"]

  connect() {
    this.addScrollArrows()
    this.checkOverflow()
    this.updateArrowVisibility()
    
    // Wait for next frame to ensure DOM is fully rendered
    requestAnimationFrame(() => {
      this.scrollActiveTabIntoView()
    })

    // Re-check overflow on window resize
    this.resizeObserver = new ResizeObserver(() => {
      this.checkOverflow()
      this.updateArrowVisibility()
    })
    this.resizeObserver.observe(this.element)

    // Listen to scroll events to update arrow visibility
    this.containerTarget.addEventListener('scroll', () => this.updateArrowVisibility())
  }

  disconnect() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  }

  addScrollArrows() {
    // Don't add if already exists
    if (this.element.querySelector('.scroll-arrow')) return

    // In RTL, visual chevrons should be reversed from their scroll direction
    const isRTL = this.isRTL
    
    const scrollLeft = document.createElement('button')
    scrollLeft.className = 'scroll-arrow scroll-left'
    scrollLeft.type = 'button'
    // In RTL, left button should show right chevron (visual flip)
    scrollLeft.innerHTML = `<i class="fa fa-chevron-${isRTL ? 'right' : 'left'}"></i>`
    scrollLeft.setAttribute('data-action', 'click->header-tabs-scroll#scrollLeft')

    const scrollRight = document.createElement('button')
    scrollRight.className = 'scroll-arrow scroll-right'
    scrollRight.type = 'button'
    // In RTL, right button should show left chevron (visual flip)
    scrollRight.innerHTML = `<i class="fa fa-chevron-${isRTL ? 'left' : 'right'}"></i>`
    scrollRight.setAttribute('data-action', 'click->header-tabs-scroll#scrollRight')

    this.element.prepend(scrollLeft)
    this.element.append(scrollRight)
  }

  checkOverflow() {
    const container = this.containerTarget
    const hasOverflow = container.scrollWidth > container.clientWidth
    
    if (hasOverflow) {
      this.element.classList.add('has-overflow')
    } else {
      this.element.classList.remove('has-overflow')
    }
  }

  get isRTL() {
    // Check body element for dir attribute
    return document.body.getAttribute('dir') === 'rtl'
  }

  updateArrowVisibility() {
    const container = this.containerTarget
    const scrollLeft = container.scrollLeft
    const scrollWidth = container.scrollWidth
    const clientWidth = container.clientWidth
    
    const leftArrow = this.element.querySelector('.scroll-arrow.scroll-left')
    const rightArrow = this.element.querySelector('.scroll-arrow.scroll-right')
    
    if (!leftArrow || !rightArrow) return
    
    // In RTL, scrollLeft behavior is reversed
    const isRTL = this.isRTL
    
    // Check if at start (hide left arrow in LTR, right arrow in RTL)
    // Use 3px threshold instead of 1px for earlier arrow hiding
    const atStart = isRTL ? scrollLeft >= -3 : scrollLeft <= 3
    // Check if at end (hide right arrow in LTR, left arrow in RTL)
    const atEnd = isRTL ? scrollLeft <= -(scrollWidth - clientWidth) + 3 : scrollLeft >= scrollWidth - clientWidth - 3
    
    if (isRTL) {
      // In RTL: hide left arrow at start (can't scroll left), right arrow at end (can't scroll right)
      leftArrow.style.display = atStart ? 'none' : 'flex'
      rightArrow.style.display = atEnd ? 'none' : 'flex'
    } else {
      // In LTR: hide left arrow at start (can't scroll left), right arrow at end (can't scroll right)
      leftArrow.style.display = atStart ? 'none' : 'flex'
      rightArrow.style.display = atEnd ? 'none' : 'flex'
    }
  }

  scrollLeft(event) {
    event.preventDefault()
    const container = this.containerTarget
    const scrollAmount = container.clientWidth * 0.7 // Scroll 70% of visible width
    
    container.scrollBy({
      left: this.isRTL ? scrollAmount : -scrollAmount,
      behavior: 'smooth'
    })
    
    // Update arrow visibility after scroll completes
    setTimeout(() => this.updateArrowVisibility(), 400)
  }

  scrollRight(event) {
    event.preventDefault()
    const container = this.containerTarget
    const scrollAmount = container.clientWidth * 0.7
    
    container.scrollBy({
      left: this.isRTL ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    })
    
    // Update arrow visibility after scroll completes
    setTimeout(() => this.updateArrowVisibility(), 400)
  }
  
  scrollActiveTabIntoView() {
    const tabsContainer = this.element.querySelector('.header-tabs')
    if (!tabsContainer) return
    
    const activeTab = tabsContainer.querySelector('.tab-button.active')
    if (!activeTab) return
    
    // Use scrollIntoView with smooth behavior and center alignment
    activeTab.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    })
  }
}
